import React, { useCallback, useRef } from 'react';

import { Box, useTheme } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { closeSecondaryPanel } from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges from 'components/hooks/useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useToast } from 'components/hooks/useToast';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  closeCreateOKRPanel,
  openCreateOKRPanel,
  saveNewlyCreatedOkr,
  useCreateObjectivePanel,
} from 'reducers/createObjectivePanelSlice';
import { okrCreationPanelStateSelector } from 'selectors/okrCreationPanelSelector';

import { SaveAndAdd } from 'assets/assetsV1/iconComponents';

import { OKR_TYPES, TOAST_STATUSES } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { sendOpenedKRPanelEvent, sendOpenedObjectivePanelEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { DrawerFooter } from 'uiToolkitV1/BottomDrawer';
import { Button } from 'uiToolkitV1/Button';
import { SplitButton } from 'uiToolkitV1/SplitButton';
import { Tooltip } from 'uiToolkitV1/Tooltip';

import { useCreateNewOKRMutation } from './datahooks/useCreateNewOKRMutation';
import { useOKRCreationValidation } from './useOKRCreationValidation';

export const Footer = ({ onCancel, onKeyResultCreation, onObjectiveCreation, renderJustButtons = false }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const toast = useToast();
  const okrDetailsRef = useRef();
  const okrDetailsFromRedux = useSelector(okrCreationPanelStateSelector);
  const page = useViewPageName();

  const history = useHistory();
  const location = useLocation();

  okrDetailsRef.current = okrDetailsFromRedux;

  const validationError = useOKRCreationValidation();
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();
  const { okrPanelPayload } = useCreateObjectivePanel();

  const dynamicCallbackToRunAfterNewOKRCreation =
    okrDetailsFromRedux?.type === OKR_TYPES.OBJECTIVE ? onObjectiveCreation : onKeyResultCreation;

  const defaultCallbackToRunAfterNewOKRCreation = useCallback(() => {
    queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsCascadedData]);
  }, [queryClient]);

  let callbackToRunAfterNewOKRCreation = useCallback(
    (okrDetails) => {
      dynamicCallbackToRunAfterNewOKRCreation(okrDetails);
      defaultCallbackToRunAfterNewOKRCreation();
    },
    [defaultCallbackToRunAfterNewOKRCreation, dynamicCallbackToRunAfterNewOKRCreation]
  );

  if (typeof dynamicCallbackToRunAfterNewOKRCreation !== 'function') {
    callbackToRunAfterNewOKRCreation = defaultCallbackToRunAfterNewOKRCreation;
  }

  const { mutate, isMutating } = useCreateNewOKRMutation();

  const isObjective = okrDetailsFromRedux?.type === OKR_TYPES.OBJECTIVE;

  const closeAndOpenPanel = useCallback(
    (newOKR, okrPanelType = OKR_TYPES.OBJECTIVE) => {
      dispatch(closeSecondaryPanel());
      dispatch(
        closeCreateOKRPanel({
          history,
          location,
        })
      );
      dispatch(
        openCreateOKRPanel({
          selectedObjective: {
            uuid: newOKR?.uuid,
            text: newOKR?.text,
            type: newOKR?.type,
            visibilityType: newOKR?.visibilityType,
          },
          selectedTimeBoundPeriod: newOKR?.timeBoundDetails,
          okrPanelType,
          parentMeasure: newOKR?.measure,
          parentMeasureType: newOKR?.measureType,
          parentProgressType: newOKR?.progressType,
          parentPost: newOKR?.parentPost,
          onKeyResultCreation,
          onObjectiveCreation,
          history,
          location,
        })
      );
    },
    [dispatch, history, location, onKeyResultCreation, onObjectiveCreation]
  );

  const handleSave = useCallback(
    async ({ okrDetails, onSuccess }) => {
      /* Queries to invalidate
      1.1 OKR ( so that parent and timeline component reflects updated )
      1.2 OKR so that progress updates can reflect ( in case of derived progress)
      2. refetch alignment section.
    */
      /*   const okrQueryKey = [QUERY_KEYS.KEY_RESULT, okrUuid];
    const alignmentQueryKey = [QUERY_KEYS.OKR_TOP_HIERARCHY, okrUuid]; */
      mutate(
        {
          okrDetails,
          okrPanelPayload,
        },
        {
          onError: (err) => {
            console.error(err.message);

            toast({
              title:
                err.message ||
                `The ${
                  isObjective
                    ? modifyString(objectivesName, StringModifier.SENTENCE_CASE)
                    : modifyString(keyResultsName, StringModifier.SENTENCE_CASE)
                } could not be created`,
              status: TOAST_STATUSES.ERROR,
              position: 'bottom-left',
            });
          },
          onSuccess: (data) => {
            const newlyCreatedOKR = data?.entity;
            /* queryClient.invalidateQueries(okrQueryKey);
            queryClient.invalidateQueries(alignmentQueryKey); */
            // queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsCascadedData]);

            dispatch(saveNewlyCreatedOkr({ ...newlyCreatedOKR }));

            toast({
              title: `The new ${
                isObjective
                  ? modifyString(objectivesName, StringModifier.SENTENCE_CASE)
                  : modifyString(keyResultsName, StringModifier.SENTENCE_CASE)
              } has been successfully created`,
              status: TOAST_STATUSES.SUCCESS,
              position: 'bottom-left',
            });
            if (typeof onSuccess === 'function') {
              onSuccess(newlyCreatedOKR);
            }
          },
        }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, isObjective, keyResultsName, objectivesName, mutate]
  );

  const handleSaveAndExit = useCallback(() => {
    const okrDetails = okrDetailsRef.current;
    const onSuccess = (okrDetails) => {
      callbackToRunAfterNewOKRCreation(okrDetails);

      dispatch(closeSecondaryPanel());
      dispatch(
        closeCreateOKRPanel({
          history,
          location,
        })
      );
    };

    handleSave({ okrDetails, onSuccess });
  }, [callbackToRunAfterNewOKRCreation, dispatch, handleSave, okrDetailsRef]);

  const handleSaveAndAddChildObjective = useCallback(() => {
    const okrDetails = okrDetailsRef.current;
    const onSuccess = (okrDetails) => {
      callbackToRunAfterNewOKRCreation(okrDetails);

      sendOpenedObjectivePanelEvent({
        eventProperties: {
          source: isObjective ? 'Add Objective Panel' : 'Add KR Panel',
          page,
          type: 'Add Objective Panel',
        },
      });
      closeAndOpenPanel(okrDetails, OKR_TYPES.OBJECTIVE);
    };

    handleSave({ okrDetails, onSuccess });
  }, [callbackToRunAfterNewOKRCreation, closeAndOpenPanel, handleSave, okrDetailsRef]);

  const handleSaveAndAddObjective = useCallback(() => {
    const okrDetails = okrDetailsRef.current;
    const onSuccess = (newlyCreatedOKRDetails) => {
      callbackToRunAfterNewOKRCreation(newlyCreatedOKRDetails);
      closeAndOpenPanel(okrDetails, OKR_TYPES.OBJECTIVE);
      sendOpenedObjectivePanelEvent({
        eventProperties: {
          source: isObjective ? 'Add Objective Panel' : 'Add KR Panel',
          page,
          type: 'Add Objective Panel',
        },
      });
    };

    handleSave({ okrDetails, onSuccess });
  }, [callbackToRunAfterNewOKRCreation, closeAndOpenPanel, handleSave, okrDetailsRef]);

  const handleSaveAndAddChildKeyResult = useCallback(() => {
    const okrDetails = okrDetailsRef.current;
    const onSuccess = (newOKR) => {
      callbackToRunAfterNewOKRCreation(newOKR);
      closeAndOpenPanel(newOKR, OKR_TYPES.KEY_RESULT);
      sendOpenedKRPanelEvent({
        eventProperties: {
          source: isObjective ? 'Add Objective Panel' : 'Add KR Panel',
          page,
          type: 'Add KR Panel',
        },
      });
    };
    handleSave({ okrDetails, onSuccess });
  }, [callbackToRunAfterNewOKRCreation, closeAndOpenPanel, handleSave, okrDetailsRef]);

  const handleSaveAndAddKeyResult = useCallback(() => {
    const okrDetails = okrDetailsRef.current;
    const onSuccess = (newOKR) => {
      callbackToRunAfterNewOKRCreation(newOKR);
      closeAndOpenPanel(okrDetails, OKR_TYPES.KEY_RESULT);
      sendOpenedKRPanelEvent({
        eventProperties: {
          source: isObjective ? 'Add Objective Panel' : 'Add KR Panel',
          page,
          type: 'Add KR Panel',
        },
      });
    };
    handleSave({ okrDetails, onSuccess });
  }, [callbackToRunAfterNewOKRCreation, closeAndOpenPanel, handleSave, okrDetailsRef]);

  const disableSubmit = validationError?.length > 0 || isMutating;

  if (renderJustButtons) {
    return (
      <>
        <Button variant="tertiary" mr="auto" onClick={onCancel}>
          Cancel
        </Button>
        <SaveAndAddComponent
          disabled={disableSubmit}
          disabledReason={validationError}
          isObjective={isObjective}
          handleSaveAndAddChildObjective={handleSaveAndAddChildObjective}
          handleSaveAndAddChildKeyResult={handleSaveAndAddChildKeyResult}
          handleSaveAndAddObjective={handleSaveAndAddObjective}
          handleSaveAndAddKeyResult={handleSaveAndAddKeyResult}
          handleSaveAndExit={handleSaveAndExit}
        />
      </>
    );
  }

  return (
    <DrawerFooter borderTopWidth="1px" h="72px" backgroundColor={theme.colors.neutral.F5F7F9}>
      <Button variant="tertiary" mr="auto" onClick={onCancel}>
        Cancel
      </Button>
      {/* <Button
        variant="outlined"
        mr={4}
        StartIcon={SaveAndExit}
        onClick={handleSaveAndExit}
        disabled={disableSubmit}
        style={{ padding: '8px 12px 8px 8px' }}
      >
        Save & Exit
      </Button> */}
      <SaveAndAddComponent
        disabled={disableSubmit}
        disabledReason={validationError}
        isObjective={isObjective}
        handleSaveAndAddChildObjective={handleSaveAndAddChildObjective}
        handleSaveAndAddChildKeyResult={handleSaveAndAddChildKeyResult}
        handleSaveAndAddObjective={handleSaveAndAddObjective}
        handleSaveAndAddKeyResult={handleSaveAndAddKeyResult}
        handleSaveAndExit={handleSaveAndExit}
      />
    </DrawerFooter>
  );
};

export const SaveAndAddComponent = React.memo(
  ({
    disabled,
    disabledReason = '',
    isObjective,
    handleSaveAndAddChildObjective,
    handleSaveAndAddObjective,
    handleSaveAndAddChildKeyResult,
    handleSaveAndAddKeyResult,
    handleSaveAndExit,
  }) => {
    const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

    /* 
    Following code is to handle access based on config and privileges.
    Starts here
  */
    const {
      canCreateObjectiveAtRootLevel,

      canCreateObjectiveUnderKeyResult,
      canCreateObjectiveUnderObjective,
      canCreateKeyResultUnderKeyResult,
      canCreateKeyResultUnderObjective,
      canCreateKeyResultAtRootLevel,
    } = useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges();

    const canCreateObjective =
      canCreateObjectiveAtRootLevel || canCreateObjectiveUnderObjective || canCreateObjectiveUnderKeyResult;

    const canCreateKeyResult =
      canCreateKeyResultAtRootLevel || canCreateKeyResultUnderObjective || canCreateKeyResultUnderKeyResult;

    const canCreateChildKeyResult = isObjective ? canCreateKeyResultUnderObjective : canCreateKeyResultUnderKeyResult;

    /* 
    Above code is to handle access based on config and privileges.
    Ends here
  */

    /* 
  
    Create key result/ objective have 4 possible options -

      Create objective panel options-
      Add a child key result (default)
      Add a child objective 
      Add another objective

    Create key result panel options -
      Add a child key result (default)
      Add another key result (default, if KR to KR cascading is disabled)
      Add a child objective

 */

    const options = [
      {
        value: 'SAVE_AND_EXIT',
        label: `Save & Exit`,
        onClick: handleSaveAndExit,
        isDisabled: disabled,
      },
    ];

    if (canCreateChildKeyResult) {
      options.push({
        value: 'CHILD_KEY_RESULT',
        label: `Save & Add Child ${keyResultsName}`,
        onClick: handleSaveAndAddChildKeyResult,
        isDisabled: disabled,
      });
    }

    if (isObjective) {
      if (canCreateObjectiveUnderObjective) {
        options.push({
          value: 'CHILD_OBJECTIVE',
          label: `Save & Add Child ${objectivesName}`,
          onClick: handleSaveAndAddChildObjective,
          isDisabled: disabled,
        });
      }

      if (canCreateObjective) {
        options.push({
          value: 'OBJECTIVE',
          label: `Save & Add another ${objectivesName}`,
          onClick: handleSaveAndAddObjective,
          isDisabled: disabled,
        });
      }
    } else {
      if (canCreateKeyResult) {
        options.push({
          value: 'KEY_RESULT',
          label: `Save & Add another ${keyResultsName}`,
          onClick: handleSaveAndAddKeyResult,
          isDisabled: disabled,
        });
      }
      if (canCreateObjectiveUnderKeyResult) {
        options.push({
          value: 'CHILD_OBJECTIVE',
          label: `Save & Add Child ${objectivesName}`,
          onClick: handleSaveAndAddChildObjective,
          isDisabled: disabled,
        });
      }
    }

    return (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <>
        {options.length > 0 ? (
          <Tooltip fontSize="14px" label={disabled ? disabledReason : ''}>
            <Box as="span">
              <SplitButton
                options={options}
                StartIcon={SaveAndAdd}
                buttonProps={{
                  withBackground: false,
                  style: {
                    height: '40px',
                    padding: '8px 6px 8px 6px',
                  },
                  disabled,
                }}
                menuButtonProps={{
                  withBackground: false,
                  style: { height: '40px', padding: '8px' },
                }}
                isDisabled={disabled}
                menuPlacement="top-end"
              />
            </Box>
          </Tooltip>
        ) : null}
      </>
    );
  }
);
